import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { InboundComposeFormValues } from '../../types';
import { PromptSelect } from './shared';
import {
  MENU_WAIT_MAX,
  MENU_WAIT_MIN,
  RE_PROMPT_MAX,
  RE_PROMPT_MIN,
  clampNumericText,
} from './shared-utils';

interface Props {
  values: InboundComposeFormValues;
  setValues: (patch: Partial<InboundComposeFormValues>) => void;
}

const OPTIONAL_FILES = [
  { key: 'welcome', label: 'Welcome file', stateKey: 'useWelcomeFile' },
  { key: 'noinput', label: 'No-input file', stateKey: 'useNoinputFile' },
  { key: 'wronginput', label: 'Wrong-input file', stateKey: 'useWronginputFile' },
  { key: 'thanks', label: 'Thanks file', stateKey: 'useThanksFile' },
] as const;

export function StepVoiceFiles({ values, setValues }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Voice files</CardTitle>
        <CardDescription>
          Pick the audio prompts the caller hears, plus how long the menu
          waits for a key press.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border border-border bg-muted/30 p-3">
          {OPTIONAL_FILES.map(({ key, label, stateKey }) => (
            <label
              key={key}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Checkbox
                checked={values[stateKey] as boolean}
                onCheckedChange={(v) =>
                  setValues({
                    [stateKey]: Boolean(v),
                  } as Partial<InboundComposeFormValues>)
                }
              />
              {label}
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {values.useWelcomeFile && (
            <PromptSelect
              label="Welcome"
              category="welcome"
              value={values.welcomeId}
              onChange={(v) => setValues({ welcomeId: v })}
            />
          )}
          <PromptSelect
            label="Menu"
            category="menu"
            required
            value={values.menuId}
            onChange={(v) => setValues({ menuId: v })}
          />
          {values.useNoinputFile && (
            <PromptSelect
              label="No input"
              category="noinput"
              value={values.noinputId}
              onChange={(v) => setValues({ noinputId: v })}
            />
          )}
          {values.useWronginputFile && (
            <PromptSelect
              label="Wrong input"
              category="wronginput"
              value={values.wronginputId}
              onChange={(v) => setValues({ wronginputId: v })}
            />
          )}
          {values.useThanksFile && (
            <PromptSelect
              label="Thanks"
              category="thanks"
              value={values.thanksId}
              onChange={(v) => setValues({ thanksId: v })}
            />
          )}
          <PromptSelect
            label="No agent available"
            category="noagent"
            required
            value={values.noAgentId}
            onChange={(v) => setValues({ noAgentId: v })}
          />
          <div className="space-y-2">
            <Label htmlFor="menu-wait">
              Menu wait time (seconds)
              <span className="ml-1 text-destructive">*</span>
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                · {MENU_WAIT_MIN}–{MENU_WAIT_MAX}
              </span>
            </Label>
            <Input
              id="menu-wait"
              inputMode="numeric"
              value={values.menuWaitTime}
              onChange={(e) =>
                setValues({
                  menuWaitTime: clampNumericText(
                    e.target.value,
                    MENU_WAIT_MIN,
                    MENU_WAIT_MAX,
                  ),
                })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="re-prompt">
              Re-prompt count
              <span className="ml-1 text-destructive">*</span>
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                · {RE_PROMPT_MIN}–{RE_PROMPT_MAX}
              </span>
            </Label>
            <Input
              id="re-prompt"
              inputMode="numeric"
              value={values.rePrompt}
              onChange={(e) =>
                setValues({
                  rePrompt: clampNumericText(
                    e.target.value,
                    RE_PROMPT_MIN,
                    RE_PROMPT_MAX,
                  ),
                })
              }
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
